import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'

import { updateTemSwitchCustomDescription } from 'actions/feature/inputs'
import { yup } from 'schemas/validation'
import { FlexRow, RadioButtonGroup, TextInput } from 'components/patterns'
import strings from 'utils/strings'
import { useSelector } from 'hooks/redux'
import { getSignal } from 'selectors/feature/inputs'
import { getIsASAMFeature } from 'selectors/feature/featureMetadata'
import { isAnalogInput, isDigitalInput, isCanInput } from 'utils/types/features'

const DEFAULT = 'default'
const CUSTOM = 'custom'

const InputCustomDescription = ({
  uri,
  inputUri,
  customDescription,
  subType,
  placeholder: placeholderProp,
}) => {
  const dispatch = useDispatch()
  const { description, supportsCustomDescriptions } = useSelector(getSignal, {
    signal: inputUri,
  })
  const isASAM = useSelector(getIsASAMFeature)
  const [userValue, setUserValue] = React.useState(
    customDescription ? CUSTOM : DEFAULT
  )

  // Sync userValue when customDescription changes (e.g., when input type changes)
  React.useEffect(() => {
    setUserValue(customDescription ? CUSTOM : DEFAULT)
  }, [customDescription])

  const handleCustomValueChange = ({ target }) => {
    setUserValue(target.value)
    if (target.value === DEFAULT) {
      dispatch(updateTemSwitchCustomDescription(uri, null))
    }
  }

  const handleCustomDescriptionChange = (value) => {
    dispatch(updateTemSwitchCustomDescription(uri, value))
  }

  // Determine label and tooltip based on input type
  const isAI = isAnalogInput(subType)
  const isDI = isDigitalInput(subType)
  const isCAN = isCanInput(subType)

  // Don't render if no input is selected yet (CAN editor is always visible, so skip for CAN)
  if (!isCAN && !inputUri) {
    return null
  }

  // Show for Analog/Digital/CAN Inputs always, or for other types only if supportsCustomDescriptions is true
  if (!isAI && !isDI && !isCAN && !supportsCustomDescriptions) {
    return null
  }

  let label
  let tooltip
  let placeholder = placeholderProp || description

  if (isAI) {
    label = strings.custom_ai_pin_name
    tooltip = strings.custom_ai_pin_tooltip
    placeholder =
      placeholderProp || description?.replace('Analog Input', 'AI Pin')
  } else if (isDI) {
    label = strings.custom_di_pin_name
    tooltip = strings.custom_di_pin_tooltip
    placeholder =
      placeholderProp || description?.replace('Digital Input', 'DI Pin')
  } else if (isCAN) {
    label = strings.custom_can_input_name
    tooltip = strings.custom_can_input_tooltip
  } else {
    label = strings.tem_custom_switch_name
    tooltip = 'You can apply a new description to this TEM Custom Switch.'
  }

  return (
    <StyledFlexRow
      alignItems="flex-end"
      isPinInput={isAI || isDI || isCAN}
      isCAN={isCAN}
      isASAM={isASAM}
      isAI={isAI}
    >
      <RadioButtonGroup
        label={label}
        tooltip={tooltip}
        currentValue={userValue}
        onChange={handleCustomValueChange}
        vertical
        buttons={[
          {
            key: DEFAULT,
            value: DEFAULT,
            text: strings.default,
            disabled: false,
          },
          {
            key: CUSTOM,
            value: CUSTOM,
            text: strings.custom,
            disabled: false,
          },
        ]}
      />
      {userValue === CUSTOM && (
        <TextInput
          name="customDescription"
          rules={yup.string().nullable().max(40)}
          placeholder={placeholder}
          value={customDescription}
          onChange={handleCustomDescriptionChange}
        />
      )}
    </StyledFlexRow>
  )
}

InputCustomDescription.propTypes = {
  uri: PropTypes.string.isRequired,
  inputUri: PropTypes.string,
  customDescription: PropTypes.string,
  subType: PropTypes.string,
  placeholder: PropTypes.string,
}

InputCustomDescription.defaultProps = {
  inputUri: null,
  customDescription: null,
  subType: null,
  placeholder: null,
}

const StyledFlexRow = styled(FlexRow)`
  &&&& {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-top: ${({ isCAN, isASAM, isAI }) =>
      isCAN || (isASAM && !isAI) ? '20px' : '0'};
    margin-bottom: ${({ isPinInput }) => (isPinInput ? '0' : '30px')};
    display: flex;
    align-items: flex-end;
    gap: 16px;
  }

  label[for='buttonGroup'] {
    margin-top: 0;
  }
`

export default InputCustomDescription
